import { create } from "zustand"
import { persist } from "zustand/middleware"
import { UserInfo } from "@services/oauthService"

export interface OAuthData {
	code: string | null
	state: string | null
	userInfo: UserInfo | null
	tokens: {
		access_token: string | null
		id_token: string | null
		refresh_token: string | null
	} | null
	callbackUrl: string | null
	timestamp: number | null
}

interface OAuthStore {
	data: OAuthData
	setAuthCode: (code: string) => void
	setState: (state: string) => void
	setUserInfo: (userInfo: UserInfo) => void
	setTokens: (tokens: OAuthData["tokens"]) => void
	setCallbackUrl: (url: string) => void
	setOAuthData: (data: Partial<OAuthData>) => void
	clearOAuthData: () => void
	isOAuthInProgress: boolean
	setOAuthInProgress: (inProgress: boolean) => void
	getOAuthData: () => OAuthData
}

const initialOAuthData: OAuthData = {
	code: null,
	state: null,
	userInfo: null,
	tokens: null,
	callbackUrl: null,
	timestamp: null
}

export const useOAuthStore = create<OAuthStore>()(
	persist(
		(set, get) => ({
			data: initialOAuthData,
			setAuthCode: (code) =>
				set((state) => ({
					data: {
						...state.data,
						code,
						timestamp: Date.now()
					}
				})),
			setState: (state) =>
				set((currentState) => ({
					data: {
						...currentState.data,
						state,
						timestamp: Date.now()
					}
				})),
			setUserInfo: (userInfo) =>
				set((state) => ({
					data: {
						...state.data,
						userInfo,
						timestamp: Date.now()
					}
				})),
			setTokens: (tokens) =>
				set((state) => ({
					data: {
						...state.data,
						tokens,
						timestamp: Date.now()
					}
				})),
			setCallbackUrl: (callbackUrl) =>
				set((state) => ({
					data: {
						...state.data,
						callbackUrl,
						timestamp: Date.now()
					}
				})),
			setOAuthData: (newData) =>
				set((state) => ({
					data: {
						...state.data,
						...newData,
						timestamp: Date.now()
					}
				})),
			clearOAuthData: () =>
				set({
					data: initialOAuthData,
					isOAuthInProgress: false
				}),
			isOAuthInProgress: false,
			setOAuthInProgress: (inProgress) =>
				set({ isOAuthInProgress: inProgress }),
			getOAuthData: () => get().data
		}),
		{
			name: "oauth-data",
			partialize: (state) => ({
				data: state.data,
				isOAuthInProgress: state.isOAuthInProgress
			})
		}
	)
)
